import { focusTrap } from '@primer/behaviors';
import { toggleExpanded, toggleHidden } from 'src/utils/dom-toggle';
import { Context, Controller } from '@hotwired/stimulus';

export default class OffCanvasDialogController extends Controller {
  public static targets = ['dialog', 'backdrop', 'content'];

  declare private dialogTarget: HTMLElement;
  declare private backdropTarget: HTMLElement;
  declare private contentTarget: HTMLElement;

  private triggerTargets!: NodeListOf<HTMLButtonElement>;
  private lastTriggerTarget: HTMLButtonElement | undefined;
  private focusController: AbortController | undefined;
  private isMobileActive: boolean = false;
  private isMobileMediaQueryList: MediaQueryList | undefined;
  private contentContainer: HTMLElement | undefined;

  public constructor(context: Context) {
    super(context);

    this.onOpen = this.onOpen.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMobileActiveChange = this.onMobileActiveChange.bind(this);
  }

  public connect(): void {
    this.triggerTargets = document.querySelectorAll(
      '[data-off-canvas-dialog-target="trigger"]',
    );

    // Make sure it's closed
    toggleHidden(this.dialogTarget, true);

    // Make sure animations are set-up
    this.backdropTarget.classList.add('opacity-0');
    this.backdropTarget.classList.remove('opacity-100');
    this.contentTarget.classList.add('translate-x-full');
    this.contentTarget.classList.remove('translate-x-0');

    this.triggerTargets.forEach((triggerTarget) => {
      triggerTarget.addEventListener('click', this.onOpen);
      toggleExpanded(triggerTarget, false);
    });

    const dialog = this.element.querySelector('[role="dialog"]');
    if (dialog instanceof HTMLElement) {
      dialog.addEventListener('keydown', this.onKeyDown);
    }

    this.contentContainer =
      document.querySelector('.peer\\/content') ?? undefined;

    const maxSize = getComputedStyle(this.contentTarget).getPropertyValue(
      '--breakpoint-xl',
    );

    this.isMobileMediaQueryList = matchMedia(`(width >= ${maxSize})`);
    this.isMobileActive = !this.isMobileMediaQueryList.matches;

    this.isMobileMediaQueryList.addEventListener(
      'change',
      this.onMobileActiveChange,
    );
  }

  public disconnect(): void {
    toggleHidden(this.dialogTarget, true);

    this.triggerTargets.forEach((triggerTarget) => {
      triggerTarget.removeEventListener('click', this.onOpen);
      toggleExpanded(triggerTarget, false);
    });

    const dialog = this.element.querySelector('[role="dialog"]');
    if (dialog instanceof HTMLElement) {
      dialog.removeEventListener('keydown', this.onKeyDown);
    }

    this.focusController?.abort('OffCanvasDialog is disconnected');
    this.isMobileMediaQueryList?.removeEventListener(
      'change',
      this.onMobileActiveChange,
    );
  }

  public get opened(): boolean {
    return this.triggerTargets.item(0).getAttribute('aria-expanded') === 'true';
  }

  public set opened(next: boolean) {
    this.triggerTargets.forEach((triggerTarget) => {
      toggleExpanded(triggerTarget, next);
    });

    if (next) {
      toggleHidden(this.dialogTarget, false);

      this.focusController = focusTrap(
        this.contentTarget,
        this.lastTriggerTarget ??
          this.triggerTargets.item(0).querySelector('button') ??
          undefined,
      );
    } else {
      setTimeout(() => {
        if (this.opened) {
          return;
        }
        toggleHidden(this.dialogTarget, true);
      }, 300);

      // Focus the menu button
      this.focusController?.abort('Off Canvas Bar is closed');
      this.lastTriggerTarget?.focus();
    }

    requestAnimationFrame(() => {
      this.backdropTarget.classList.toggle('opacity-0', !next);
      this.backdropTarget.classList.toggle('opacity-100', next);

      this.contentTarget.classList.toggle('translate-x-full', !next);
      this.contentTarget.classList.toggle('translate-x-0', next);

      document.body.classList.toggle('overflow-hidden!', next);
    });
  }

  private onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.opened = false;
    }
  }

  public onOpen(event: Event): void {
    this.lastTriggerTarget =
      event.currentTarget instanceof HTMLButtonElement
        ? event.currentTarget
        : undefined;

    this.opened = true;
  }

  public onClose(): void {
    this.opened = false;
  }

  public onBackdrop(): void {
    this.opened = false;
  }

  private onMobileActiveChange(ev: MediaQueryListEvent): void {
    this.isMobileActive = !ev.matches;
    /*this.contentContainer?.classList.toggle(
      'scroll-pt-[100px]',
      !this.isMobileActive || !this.opened,
    );*/

    if (this.opened) {
      if (this.isMobileActive) {
        document.body.classList.toggle('overflow-hidden!', true);
        this.contentContainer?.classList.toggle('overflow-hidden!', true);
      } else {
        // Close dialog when it's hidden (on lg)
        this.opened = false;
      }
    }
  }
}
